import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { AlertTriangle, Loader2, Eye, EyeOff, ArrowLeft, Building2, Sparkles } from 'lucide-react';
import VerifyCodePage from './VerifyCodePage';
import axios from 'axios';

import API from './apiConfig';

export default function RegisterPage({ onSwitch }) {
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', company: '', email: '', password: '', referral_code: '', invite_code: '', early_bird: false });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCompany, setInviteCompany] = useState(null);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState(null);
  const [earlyBirdPending, setEarlyBirdPending] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    const ref = params.get('ref');
    if (invite) {
      setForm(prev => ({ ...prev, invite_code: invite }));
      axios.get(`${API}/api/company/invite-info/${invite}`)
        .then(({ data }) => {
          setInviteCompany(data.company_name);
          setForm(prev => ({ ...prev, company: data.company_name }));
        })
        .catch(() => setError('Ungültiger Einladungslink.'));
    }
    if (ref) {
      setForm(prev => ({ ...prev, referral_code: ref }));
    }
  }, []);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company.trim()) { setError('Firmenname ist erforderlich'); return; }
    if (form.password.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    if (!agbAccepted) { setError('Bitte akzeptieren Sie die AGB und Datenschutzerklärung'); return; }
    setLoading(true);
    const result = await register(form);
    setLoading(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    // Early Bird: direkt Warteseite zeigen (kein Login, kein 2FA)
    if (result.early_bird_pending || form.early_bird) {
      setEarlyBirdPending(true);
      return;
    }
    if (result.needs_verification) {
      setPendingVerifyEmail(result.email || form.email);
    }
  };

  if (earlyBirdPending) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <img src="/logo.png" alt="LAIzyBlueprint" className="h-24 mx-auto mb-6" />
          <div className="p-6 bg-amber-500/5 border border-amber-500/20 rounded-xl">
            <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white font-mono mb-3">Early Bird Zugang beantragt</h2>
            <p className="text-zinc-400 text-sm font-mono leading-relaxed mb-4">
              Vielen Dank für Ihr Interesse! Ihr Zugang wird derzeit geprüft und in Kürze freigeschaltet.
            </p>
            <p className="text-zinc-500 text-xs font-mono">
              Sie erhalten eine Benachrichtigung, sobald Ihr Zugang aktiv ist. Bitte gedulden Sie sich.
            </p>
          </div>
          <button onClick={() => onSwitch('login')} className="mt-6 text-blue-400 hover:text-blue-300 text-sm font-mono transition-colors">
            Zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  if (pendingVerifyEmail) {
    return (
      <VerifyCodePage
        email={pendingVerifyEmail}
        purpose="register"
        onBack={() => setPendingVerifyEmail(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 relative">
      <button onClick={() => onSwitch('landing')} data-testid="register-back-btn"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-200 text-sm font-mono transition-colors">
        <ArrowLeft className="w-4 h-4" /> Zurück zur Webseite
      </button>
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="LAIzyBlueprint" className="h-24 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white font-mono tracking-wider">Registrieren</h1>
          {inviteCompany && (
            <div className="mt-3 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <Building2 className="w-4 h-4 text-blue-400" />
              <span className="text-blue-300 text-sm font-mono">Einladung: {inviteCompany}</span>
            </div>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div data-testid="register-error" className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">Vorname</label>
              <input data-testid="register-firstname" type="text" value={form.first_name} onChange={e => update('first_name', e.target.value)} required
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">Nachname</label>
              <input data-testid="register-lastname" type="text" value={form.last_name} onChange={e => update('last_name', e.target.value)} required
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">Firma *</label>
            <input data-testid="register-company" type="text" value={form.company}
              onChange={e => !inviteCompany && update('company', e.target.value)}
              readOnly={!!inviteCompany} required
              className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-sm font-mono focus:outline-none ${
                inviteCompany ? 'border-blue-500/40 text-blue-300 cursor-not-allowed' : 'border-zinc-700 text-white focus:border-blue-500'
              }`} />
            {!inviteCompany && (
              <p className="text-[10px] text-zinc-600 mt-0.5 font-mono">Ihre Firma wird beim Registrieren angelegt. Sie werden Firmeninhaber.</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">E-Mail</label>
            <input data-testid="register-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} required
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none" placeholder="name@firma.de" />
          </div>
          <div className="relative">
            <label className="block text-xs text-zinc-500 mb-1 font-mono">Passwort (min. 8 Zeichen)</label>
            <input data-testid="register-password" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)} required
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none pr-10" />
            <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-3 top-7 text-zinc-500 hover:text-zinc-300">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">Empfehlungscode (optional)</label>
            <input data-testid="register-referral" type="text" value={form.referral_code} onChange={e => update('referral_code', e.target.value)}
              className={`w-full px-3 py-2 bg-zinc-900 border rounded-lg text-sm font-mono focus:outline-none ${
                form.referral_code ? 'border-blue-500/40 text-blue-300' : 'border-zinc-700 text-white focus:border-blue-500'
              }`} placeholder="z.B. partner2026" />
          </div>

          {/* Early Bird */}
          {!inviteCompany && (
            <div
              onClick={() => update('early_bird', !form.early_bird)}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                form.early_bird
                  ? 'bg-amber-500/10 border-amber-500/30'
                  : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-600'
              }`}
              data-testid="early-bird-toggle"
            >
              <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                form.early_bird ? 'bg-amber-500 border-amber-500' : 'border-zinc-600 bg-zinc-800'
              }`}>
                {form.early_bird && <svg className="w-2.5 h-2.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                  <span className="font-mono text-xs font-medium text-amber-400">Early Bird Zugang anfragen</span>
                </div>
                <p className="text-[10px] text-zinc-500 font-mono mt-0.5 leading-relaxed">
                  Kostenlosen Testzugang mit bis zu 10 Projekten erhalten. Freischaltung durch unser Team.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2.5 py-1">
            <input type="checkbox" id="agb-checkbox" data-testid="register-agb-checkbox"
              checked={agbAccepted} onChange={e => setAgbAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 accent-blue-500 cursor-pointer" />
            <label htmlFor="agb-checkbox" className="text-[11px] text-zinc-400 leading-relaxed cursor-pointer">
              Ich akzeptiere die <a href="/agb" target="_blank" rel="noopener" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">AGB</a> und die <a href="/datenschutz" target="_blank" rel="noopener" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Datenschutzerklärung</a>.
            </label>
          </div>
          <button data-testid="register-submit" type="submit" disabled={loading || !agbAccepted}
            className={`w-full py-2.5 font-mono text-sm rounded-lg transition-colors flex items-center justify-center gap-2 ${
              form.early_bird
                ? 'bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 text-white'
                : 'bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white'
            }`}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Registrieren...</> : form.early_bird ? 'Early Bird Zugang anfragen' : 'Konto erstellen'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button data-testid="switch-login" onClick={() => onSwitch('login')} className="text-blue-400 hover:text-blue-300 text-sm font-mono transition-colors">
            Bereits registriert? Anmelden
          </button>
        </div>
      </div>
    </div>
  );
}
